import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { IDonorShieldDirect } from '@nkr/models/index';
import { GridWrapper, HeaderWrapper } from './styles';
import { DonorShieldDirectCategory, DonorShieldDirectViewType, OrganType } from '@nkr/enums/index';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi, ICellRendererParams, GridReadyEvent, FirstDataRenderedEvent, IHeaderParams, ColDef, SortChangedEvent, RowDataChangedEvent, GridSizeChangedEvent, BodyScrollEvent, ValueGetterFunc, ValueGetterParams, GridColumnsChangedEvent } from 'ag-grid-community';
import ActionsDropdown from './actions-dropdown';
import DonorShieldDirectAction from './action';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Utility } from '@nkr/services/utility';
import { Glyphicon } from 'react-bootstrap';

function formatOrganType(organType: number | string | null | undefined): string {
    if (organType === OrganType.Kidney || organType === 0 || organType === '0' || organType === 'Kidney') {
        return 'Kidney';
    }
    if (organType === OrganType.Liver || organType === 1 || organType === '1' || organType === 'Liver') {
        return 'Liver';
    }
    if (organType == null || organType === '') {
        return '--';
    }
    return String(organType);
}

interface IProps {
    list: IDonorShieldDirect[],
    isLoading: boolean,
    isSuperUser: boolean,
    gridApi: GridApi,
    columnApi: ColumnApi,
    selectedTab: DonorShieldDirectCategory,
    onSetGridComponents: (params: GridReadyEvent) => void,
    onActionRequest: (id: number, viewType: DonorShieldDirectViewType) => void
}

const DonorShieldDirectGrid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const handleItemSelected = (requestId: number, actionType: DonorShieldDirectAction) => {
        switch (actionType) {
            case DonorShieldDirectAction.Edit:
                props.onActionRequest(requestId, DonorShieldDirectViewType.EditDonor)
                break;

            case DonorShieldDirectAction.Delete:
                props.onActionRequest(requestId, DonorShieldDirectViewType.DeleteDonor)
                break;

            case DonorShieldDirectAction.ApplyforWage:
                props.onActionRequest(requestId, DonorShieldDirectViewType.ApplyWage)
                break;

            case DonorShieldDirectAction.OrderStreamlinedSerology:
                props.onActionRequest(requestId, DonorShieldDirectViewType.OrderStreamlinedSerology)
                break;

            case DonorShieldDirectAction.EditWageAndTravel:
                props.onActionRequest(requestId, DonorShieldDirectViewType.EditWageAndTravel)
                break;
        }
    };

    const getColumnChanges = () => {
        let columns: ColDef[] = [
            {
                colId: '1',
                field: 'rank',
                headerName: 'Rank',
                headerComponentFramework: () =>
                    <div>R#</div>,
                sortable: false,
                width: 60,
                valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
            },
            {
                field: 'organType',
                headerName: 'Organ',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Organ</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                valueGetter: (params: ValueGetterParams) => formatOrganType(params.data?.organType)
            },
            {
                field: 'isAdvancedReimbursementInvitee',
                headerName: 'AR invitee',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>AR<br />invitee</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 90,
                valueGetter: (params: ValueGetterParams) => params.data?.isAdvancedReimbursementInvitee ? 'Yes' : ''
            },
            {
                field: 'donorAlias',
                headerName: 'donorAlias',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donor<br />Alias</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 100,
                valueGetter: (params: ValueGetterParams) => {
                    let data = params.data as IDonorShieldDirect;
                    return data?.donorAlias ? data.donorAlias : data?.donorName;
                }
            },
            {
                field: 'donorUnosId',
                headerName: 'D UNOS Id',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donor<br />UNOS ID</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'donorAboType',
                headerName: 'D ABO',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donor<br />ABO</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'donorGender',
                headerName: 'D Gender',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donor<br />Gender</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'donorAge',
                headerName: 'D Age',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Donor<br />Age</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'recipientAlias',
                headerName: 'R Alias',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Recipient<br />Alias</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'recipientAboType',
                headerName: 'R ABO',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Recipient<br />ABO</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'recipientGender',
                headerName: 'R Gender',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Recipient<br />Gender</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'recipientAge',
                headerName: 'R Age',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Recipient<br />Age</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
            },
            {
                field: 'surgeryDate',
                headerName: 'TXP Date',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Transplant<br />Date</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
                comparator: Utility.dateComparator
            },
            {
                field: 'surgeryDate',
                headerName: 'Wage & Expense',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>
                        <HeaderWrapper>Wage &<br />Expense</HeaderWrapper>
                    </SortableHeaderComponent>,
                width: 110,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorShieldDirect;
    
                    if (data.watrRequest != null)
                        return (
                            <Glyphicon glyph='ok' />
                        );
    
                    return null;
                },
            },
            {
                field: 'actionId',
                headerName: 'Actions',
                headerComponentFramework: (params: IHeaderParams) =>
                    <div>Actions</div>,
                width: 100,
                cellClass: 'action-menu',
                sortable: false,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IDonorShieldDirect;

                    return (
                        <ActionsDropdown {...params}
                            request={data}
                            isNkrSuper={props.isSuperUser}
                            showBsMenu={false}
                            updateModalView={props.onActionRequest}
                            onMenuToggle={handleMenuToggle}
                            onItemSelected={handleItemSelected}
                        />
                    );
                }
            }
        ];

        return columns;
    };

    const onGridReady = (e: GridReadyEvent) => {
        props.onSetGridComponents(e);
        e.api.sizeColumnsToFit();
    };

    const onDataFirstRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onGridColumnsChanged = (e: GridColumnsChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const columnDefs = useMemo(() => getColumnChanges(), [props.selectedTab]);

    // Withholding rowData (undefined) while loading keeps ag-grid on its
    // built-in loading overlay; only handing it a real array (even []) once
    // loading finishes lets ag-grid's own no-rows overlay kick in correctly.
    const rowData = useMemo(() => props.isLoading ? undefined : props.list, [props.isLoading, props.list]);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap' height={'72vh'}>
            <AgGridReact
                defaultColDef={{
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={42}
                rowHeight={32}
                // enableCellChangeFlash={true}
                suppressCellSelection={true}
                rowStyle={{ overflow: 'visible', cursor: 'pointer' }}
                noRowsOverlayComponentFramework={() => <div>No Donor Shield Direct data found...</div>}
                loadingOverlayComponentFramework={() => <div style={{ fontSize: 22 }}>Loading donor shield records, one moment please...</div>}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onDataFirstRendered}
                onSortChanged={onSortChanged}
                onBodyScroll={onBodyScroll}
                onRowDataChanged={onRowDataChanged}
                onGridColumnsChanged={onGridColumnsChanged}
            >
            </AgGridReact>
        </GridWrapper>
    );
}

export default DonorShieldDirectGrid;